import type { I18n } from '@learnforge/i18n';
import { attachPopover, type PopoverHandle } from './popover';

export interface DataTableColumn<K extends string = string> {
  id: string;
  labelKey: K;
  format?(value: unknown): string; // default: String(value)
  /** When true AND the table was created with `onToggle`, boolean cells in
   * this column render as live checkboxes instead of formatted text. */
  editable?: boolean;
}
export interface DataTableStat {
  label: string; // already-translated by the caller
  value: string;
}
export interface CreateDataTableOptions<K extends string = string> {
  i18n: I18n<K>;
  columns: readonly DataTableColumn<K>[];
  getRows(): ReadonlyArray<Record<string, unknown>>;
  getStats(): readonly DataTableStat[];
  defaultVisible?: readonly string[]; // column ids; omit = all visible, picker hidden
  rowNumbers?: boolean; // render a leading 1-based '#' column
  maxBodyHeight?: number; // px, default 260
  /** Cell-checkbox change callback. Its presence is the table-level
   * "editable" switch: omit it and `editable` columns fall back to text. */
  onToggle?(rowIndex: number, columnId: string, checked: boolean): void;
}
export interface DataTableHandle {
  element: HTMLElement; // scrollable wrapper containing the <table>
  controls: HTMLElement; // span holding the Σ and ▦ popover hosts — place in a card header
  statsButton: HTMLButtonElement;
  columnsButton: HTMLButtonElement;
  update(): void; // re-read getRows()/getStats()
  setVisible(ids: readonly string[]): void;
  destroy(): void;
}

/** Default cell formatter used when a column has no `format`: `String(value)`. */
function defaultFormat(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'symbol'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * A scrollable data table with i18n headers, a Σ stats popover, and a ▦
 * column-visibility picker popover. Consumers own the row data (via
 * `getRows`) and re-render on demand with `update()`.
 */
export function createDataTable<K extends string = string>(
  opts: CreateDataTableOptions<K>,
): DataTableHandle {
  const allIds = opts.columns.map((c) => c.id);
  const visible = new Set<string>(opts.defaultVisible ?? allIds);

  const element = document.createElement('div');
  element.className = 'kit-table-wrap';
  element.style.maxHeight = `${opts.maxBodyHeight ?? 260}px`;

  const table = document.createElement('table');
  table.className = 'kit-table';
  const thead = document.createElement('thead');
  const tbody = document.createElement('tbody');
  table.append(thead, tbody);
  element.appendChild(table);

  const controls = document.createElement('span');
  controls.className = 'kit-table-controls';

  const statsButton = document.createElement('button');
  statsButton.type = 'button';
  statsButton.className = 'kit-table-btn';
  statsButton.textContent = 'Σ';
  statsButton.setAttribute('aria-label', 'Statistics');

  const columnsButton = document.createElement('button');
  columnsButton.type = 'button';
  columnsButton.className = 'kit-table-btn';
  columnsButton.textContent = '▦';
  columnsButton.setAttribute('aria-label', 'Columns');
  columnsButton.hidden = !opts.defaultVisible || opts.defaultVisible.length >= allIds.length;

  controls.append(statsButton, columnsButton);
  const statsPopover: PopoverHandle = attachPopover(statsButton, {
    panelClassName: 'kit-table-stats',
  });
  const columnsPopover: PopoverHandle = attachPopover(columnsButton, {
    panelClassName: 'kit-table-picker',
  });

  const visibleColumns = () => opts.columns.filter((c) => visible.has(c.id));

  function renderHead(): void {
    const tr = document.createElement('tr');
    if (opts.rowNumbers) {
      const th = document.createElement('th');
      th.textContent = '#';
      tr.appendChild(th);
    }
    for (const col of visibleColumns()) {
      const th = document.createElement('th');
      th.textContent = opts.i18n.t(col.labelKey);
      tr.appendChild(th);
    }
    thead.replaceChildren(tr);
  }

  function renderBody(): void {
    const rows = opts.getRows();
    const frag = document.createDocumentFragment();
    rows.forEach((row, i) => {
      const tr = document.createElement('tr');
      if (opts.rowNumbers) {
        const td = document.createElement('td');
        td.textContent = String(i + 1);
        tr.appendChild(td);
      }
      for (const col of visibleColumns()) {
        const td = document.createElement('td');
        const v = row[col.id];
        if (col.editable && opts.onToggle) {
          const cb = document.createElement('input');
          cb.type = 'checkbox';
          cb.className = 'kit-table-check';
          cb.checked = Boolean(v);
          cb.setAttribute('aria-label', `${opts.i18n.t(col.labelKey)} ${i + 1}`);
          cb.addEventListener('change', () => opts.onToggle!(i, col.id, cb.checked));
          td.appendChild(cb);
        } else {
          td.textContent = col.format ? col.format(v) : defaultFormat(v);
        }
        tr.appendChild(td);
      }
      frag.appendChild(tr);
    });
    tbody.replaceChildren(frag);
  }

  function renderStats(): void {
    const dl = document.createElement('dl');
    for (const stat of opts.getStats()) {
      const dt = document.createElement('dt');
      dt.textContent = stat.label;
      const dd = document.createElement('dd');
      dd.textContent = stat.value;
      dl.append(dt, dd);
    }
    statsPopover.panel.replaceChildren(dl);
  }

  function renderPicker(): void {
    const list = document.createElement('div');
    for (const col of opts.columns) {
      const label = document.createElement('label');
      label.className = 'kit-table-picker-row';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = visible.has(col.id);
      cb.dataset['colId'] = col.id;
      cb.addEventListener('change', () => {
        if (cb.checked) visible.add(col.id);
        else visible.delete(col.id);
        renderHead();
        renderBody();
      });
      const span = document.createElement('span');
      span.textContent = opts.i18n.t(col.labelKey);
      label.append(cb, span);
      list.appendChild(label);
    }
    columnsPopover.panel.replaceChildren(list);
  }

  function renderAll(): void {
    renderHead();
    renderBody();
    renderStats();
    renderPicker();
  }
  renderAll();
  const unsub = opts.i18n.subscribe(renderAll);

  return {
    element,
    controls,
    statsButton,
    columnsButton,
    update(): void {
      renderBody();
      renderStats();
    },
    setVisible(ids): void {
      visible.clear();
      for (const id of ids) if (allIds.includes(id)) visible.add(id);
      renderHead();
      renderBody();
      renderPicker();
    },
    destroy(): void {
      unsub();
      statsPopover.destroy();
      columnsPopover.destroy();
      element.remove();
      controls.remove();
    },
  };
}
